import React, { useState, useEffect, useCallback } from 'react';
import {
  getCommunityConfigs, createCommunityConfig, updateCommunityConfig, deleteCommunityConfig,
  getExperienceLevels, createExperienceLevel, updateExperienceLevel, deleteExperienceLevel,
  getGovernmentIdTypes, createGovernmentIdType, updateGovernmentIdType, deleteGovernmentIdType,
  getScholarTypeConfigs, createScholarTypeConfig, updateScholarTypeConfig, deleteScholarTypeConfig,
  getPlatformReligions,
  getOperationalCities, createOperationalCity, updateOperationalCity, deleteOperationalCity,
} from '../services/adminService';
import { pageContainer, cardStyle, thStyle, tdStyle, badgeStyle, primaryBtn, secondaryBtn, inputStyle } from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';

/* ─── reusable inline‑modal ─────────────────────────────────── */
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { ...cardStyle, width: 480, maxHeight: '80vh', overflow: 'auto' };
const row: React.CSSProperties = { marginBottom: 14 };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: 4 };
const toggle: React.CSSProperties = { width: 18, height: 18, accentColor: 'hsl(var(--primary))' };
const delBtn: React.CSSProperties = { ...secondaryBtn, color: '#ef4444', borderColor: '#fecaca', fontSize: 12, padding: '6px 14px' };

/* ─── Tab bar styles ──────────────────────────────────────── */
const tabBar: React.CSSProperties = { display: 'flex', gap: 4, marginBottom: 28, borderBottom: '1px solid hsl(var(--border))', paddingBottom: 0 };
const tab = (active: boolean): React.CSSProperties => ({
  padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
  borderBottom: active ? '2px solid hsl(var(--primary))' : '2px solid transparent',
  background: 'transparent', color: active ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
  transition: 'all 150ms',
});

const TABS = ['Communities', 'Experience Levels', 'Gov. ID Types', 'Scholar Types', 'Operational Cities', 'Ticket Categories', 'Priority Matrix', 'Escalation Matrix'] as const;
type Tab = typeof TABS[number];

const PlatformConfigs = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Communities');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [religions, setReligions] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();

  /* form fields (superset for all tabs) */
  const [fName, setFName] = useState('');
  const [fValue, setFValue] = useState('');
  const [fLabel, setFLabel] = useState('');
  const [fDesc, setFDesc] = useState('');
  const [fIcon, setFIcon] = useState('');
  const [fReligion, setFReligion] = useState('');
  const [fSort, setFSort] = useState(0);
  const [fActive, setFActive] = useState(true);

  const [filterReligion, setFilterReligion] = useState('');

  const fetchReligions = useCallback(async () => { const r = await getPlatformReligions(); setReligions(r); }, []);

  const fetchData = useCallback(async () => {
    if (activeTab === 'Priority Matrix' || activeTab === 'Escalation Matrix') {
      setLoading(false);
      return;
    }
    setLoading(true);
    let items: any[] = [];
    if (activeTab === 'Communities') items = await getCommunityConfigs();
    else if (activeTab === 'Experience Levels') items = await getExperienceLevels();
    else if (activeTab === 'Gov. ID Types') items = await getGovernmentIdTypes();
    else if (activeTab === 'Scholar Types') items = await getScholarTypeConfigs();
    else if (activeTab === 'Operational Cities') items = await getOperationalCities();
    else if (activeTab === 'Ticket Categories') {
      const { getConfig } = await import('../services/adminService');
      const configs = await getConfig('general');
      const categoriesCfg = configs.find(c => c.key === 'SUPPORT_TICKET_CATEGORIES');
      if (categoriesCfg) {
        try {
          const arr = JSON.parse(categoriesCfg.value);
          items = arr.map((val: string) => ({ id: val, value: val, label: val }));
        } catch {
          items = [];
        }
      } else {
        items = [
          { id: 'TECHNICAL', value: 'TECHNICAL', label: 'TECHNICAL' },
          { id: 'PAYMENT', value: 'PAYMENT', label: 'PAYMENT' },
          { id: 'BOOKING', value: 'BOOKING', label: 'BOOKING' },
          { id: 'ACCOUNT', value: 'ACCOUNT', label: 'ACCOUNT' },
          { id: 'OTHER', value: 'OTHER', label: 'OTHER' }
        ];
      }
    }
    setData(items);
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { fetchReligions(); }, [fetchReligions]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredData = data.filter(item => {
    if (activeTab === 'Communities' || activeTab === 'Scholar Types') {
      return filterReligion ? item.religion === filterReligion : true;
    }
    return true; // No religion filter for other tabs
  });

  const openCreate = () => {
    setEditing(null); setFName(''); setFValue(''); setFLabel(''); setFDesc(''); setFIcon(''); setFReligion(''); setFSort(0); setFActive(true);
    setShowModal(true);
  };

  const openEdit = (item: any) => {
    setEditing(item);
    setFName(item.name || ''); setFValue(item.state || item.value || ''); setFLabel(item.label || '');
    setFDesc(item.description || ''); setFIcon(item.icon || '');
    setFReligion(item.religion || ''); setFSort(item.sortOrder ?? 0); setFActive(item.isActive ?? true);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (activeTab === 'Communities') {
        const payload = { religion: fReligion, name: fName, isActive: fActive, sortOrder: fSort };
        editing ? await updateCommunityConfig(editing.id, payload) : await createCommunityConfig(payload);
      } else if (activeTab === 'Experience Levels') {
        const payload = { value: fValue, label: fLabel, description: fDesc, isActive: fActive, sortOrder: fSort };
        editing ? await updateExperienceLevel(editing.id, payload) : await createExperienceLevel(payload);
      } else if (activeTab === 'Gov. ID Types') {
        const payload = { value: fValue, label: fLabel, description: fDesc, icon: fIcon, isActive: fActive, sortOrder: fSort };
        editing ? await updateGovernmentIdType(editing.id, payload) : await createGovernmentIdType(payload);
      } else if (activeTab === 'Scholar Types') {
        const payload = { value: fValue, label: fLabel, description: fDesc, icon: fIcon, religion: fReligion, isActive: fActive, sortOrder: fSort };
        editing ? await updateScholarTypeConfig(editing.id, payload) : await createScholarTypeConfig(payload);
      } else if (activeTab === 'Operational Cities') {
        const payload = { name: fName, state: fValue, isActive: fActive };
        editing ? await updateOperationalCity(editing.id, payload) : await createOperationalCity(payload);
      } else if (activeTab === 'Ticket Categories') {
        const { getConfig, updateConfig } = await import('../services/adminService');
        const configs = await getConfig('general');
        const categoriesCfg = configs.find(c => c.key === 'SUPPORT_TICKET_CATEGORIES');
        let currentArray = ["TECHNICAL", "PAYMENT", "BOOKING", "ACCOUNT", "OTHER"];
        if (categoriesCfg) {
          try { currentArray = JSON.parse(categoriesCfg.value); } catch {}
        }
        
        const valToUse = (fValue || fLabel).toUpperCase().trim();
        if (editing) {
          const idx = currentArray.indexOf(editing.id);
          if (idx !== -1) {
            currentArray[idx] = valToUse;
          }
        } else {
          if (!currentArray.includes(valToUse)) {
            currentArray.push(valToUse);
          }
        }
        const payload = [{ key: 'SUPPORT_TICKET_CATEGORIES', value: JSON.stringify(currentArray), category: 'general' }];
        await updateConfig(payload);
      }
      showToast(editing ? 'Updated successfully' : 'Created successfully');
      setShowModal(false); fetchData();
    } catch { showToast('Save failed', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      if (activeTab === 'Communities') await deleteCommunityConfig(id);
      else if (activeTab === 'Experience Levels') await deleteExperienceLevel(id);
      else if (activeTab === 'Gov. ID Types') await deleteGovernmentIdType(id);
      else if (activeTab === 'Scholar Types') await deleteScholarTypeConfig(id);
      else if (activeTab === 'Operational Cities') await deleteOperationalCity(id);
      else if (activeTab === 'Ticket Categories') {
        const { getConfig, updateConfig } = await import('../services/adminService');
        const configs = await getConfig('general');
        const categoriesCfg = configs.find(c => c.key === 'SUPPORT_TICKET_CATEGORIES');
        let currentArray = ["TECHNICAL", "PAYMENT", "BOOKING", "ACCOUNT", "OTHER"];
        if (categoriesCfg) {
          try { currentArray = JSON.parse(categoriesCfg.value); } catch {}
        }
        const filtered = currentArray.filter(c => c !== id);
        const payload = [{ key: 'SUPPORT_TICKET_CATEGORIES', value: JSON.stringify(filtered), category: 'general' }];
        await updateConfig(payload);
      }
      showToast('Deleted'); fetchData();
    } catch { showToast('Delete failed', 'error'); }
  };

  /* dynamic columns per tab */
  const columns: Record<string, { key: string; label: string }[]> = {
    Communities: [{ key: 'religion', label: 'RELIGION' }, { key: 'name', label: 'NAME' }],
    'Experience Levels': [{ key: 'value', label: 'VALUE' }, { key: 'label', label: 'LABEL' }, { key: 'description', label: 'DESCRIPTION' }],
    'Gov. ID Types': [{ key: 'value', label: 'VALUE' }, { key: 'label', label: 'LABEL' }, { key: 'icon', label: 'ICON' }],
    'Scholar Types': [{ key: 'religion', label: 'RELIGION' }, { key: 'value', label: 'VALUE' }, { key: 'label', label: 'LABEL' }],
    'Operational Cities': [{ key: 'name', label: 'CITY NAME' }, { key: 'state', label: 'STATE' }],
    'Ticket Categories': [{ key: 'value', label: 'CATEGORY CODE' }, { key: 'label', label: 'DISPLAY NAME' }],
  };

  const needsReligion = activeTab === 'Communities' || activeTab === 'Scholar Types';
  const needsValueLabel = activeTab !== 'Communities' && activeTab !== 'Operational Cities';

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader breadcrumb="Dashboard › Platform Configs" title="Platform Configurations" subtitle="Manage communities, experience levels, government ID types, and scholar types." onRefresh={fetchData}
        extraButtons={(activeTab !== 'Priority Matrix' && activeTab !== 'Escalation Matrix') && <button style={primaryBtn} onClick={openCreate}>+ New</button>} />

      <div style={tabBar}>
        {TABS.map(t => <button key={t} style={tab(activeTab === t)} onClick={() => setActiveTab(t)}>{t}</button>)}
      </div>

      <div style={cardStyle}>
        {activeTab === 'Priority Matrix' ? (
          <PriorityMatrixPanel showToast={showToast} />
        ) : activeTab === 'Escalation Matrix' ? (
          <EscalationMatrixPanel showToast={showToast} />
        ) : (
          <>
            {(activeTab === 'Communities' || activeTab === 'Scholar Types') && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <select 
                  style={{ ...inputStyle, width: 220 }} 
                  value={filterReligion} 
                  onChange={e => setFilterReligion(e.target.value)}
                >
                  <option value="">All Religions</option>
                  {religions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{(columns[activeTab as any] || []).map((c: any) => <th key={c.key} style={thStyle}>{c.label}</th>)}<th style={thStyle}>ACTIVE</th><th style={thStyle}>ORDER</th><th style={thStyle}>ACTIONS</th></tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={(columns[activeTab as any] || []).length + 3} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan={(columns[activeTab as any] || []).length + 3} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No items found.</td></tr>
                ) : filteredData.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                    {(columns[activeTab as any] || []).map((c: any) => (
                      <td key={c.key} style={tdStyle}>
                        {c.key === 'religion' ? <span style={badgeStyle}>{item[c.key]}</span> : (item[c.key] || '—')}
                      </td>
                    ))}
                    <td style={tdStyle}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: item.isActive ? '#f0fdf4' : '#fef2f2', color: item.isActive ? '#15803d' : '#dc2626' }}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={tdStyle}>{item.sortOrder ?? '—'}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button style={{ ...secondaryBtn, fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(item)}>Edit</button>
                        <button style={delBtn} onClick={() => handleDelete(item.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'hsl(var(--foreground))' }}>
              {editing ? 'Edit' : 'Create'} {activeTab.replace(/s$/, '')}
            </h3>

            {needsReligion && (
              <div style={row}>
                <label style={lbl}>Religion *</label>
                <select style={{ ...inputStyle }} value={fReligion} onChange={e => setFReligion(e.target.value)}>
                  <option value="">Select…</option>
                  {religions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            {(activeTab === 'Communities' || activeTab === 'Operational Cities') && (
              <div style={row}>
                <label style={lbl}>{activeTab === 'Operational Cities' ? 'City Name *' : 'Name *'}</label>
                <input style={inputStyle} value={fName} onChange={e => setFName(e.target.value)} />
              </div>
            )}

            {activeTab === 'Operational Cities' && (
              <div style={row}>
                <label style={lbl}>State *</label>
                <input style={inputStyle} value={fValue} onChange={e => setFValue(e.target.value)} />
              </div>
            )}

            {needsValueLabel && <>
              <div style={row}><label style={lbl}>Value *</label><input style={inputStyle} value={fValue} onChange={e => setFValue(e.target.value)} /></div>
              <div style={row}><label style={lbl}>Label *</label><input style={inputStyle} value={fLabel} onChange={e => setFLabel(e.target.value)} /></div>
            </>}

            {activeTab !== 'Operational Cities' && (
              <div style={row}><label style={lbl}>Description</label><input style={inputStyle} value={fDesc} onChange={e => setFDesc(e.target.value)} /></div>
            )}

            {(activeTab === 'Gov. ID Types' || activeTab === 'Scholar Types') && (
              <div style={row}><label style={lbl}>Icon</label><input style={inputStyle} value={fIcon} onChange={e => setFIcon(e.target.value)} placeholder="emoji or icon name" /></div>
            )}

            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              {activeTab !== 'Operational Cities' && (
                <div style={{ flex: 1 }}><label style={lbl}>Sort Order</label><input type="number" style={inputStyle} value={fSort} onChange={e => setFSort(Number(e.target.value))} /></div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: activeTab === 'Operational Cities' ? 0 : 18 }}>
                <input type="checkbox" checked={fActive} onChange={e => setFActive(e.target.checked)} style={toggle} /><label style={{ fontSize: 13 }}>Active</label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button style={secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Priority Matrix Configuration Panel ────────────────────────── */
const PriorityMatrixPanel = ({ showToast }: { showToast: any }) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [matrix, setMatrix] = useState<Record<string, 'LOW' | 'MEDIUM' | 'HIGH'>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const { getConfig } = await import('../services/adminService');
      const configs = await getConfig('general');

      const categoriesCfg = configs.find(c => c.key === 'SUPPORT_TICKET_CATEGORIES');
      let loadedCats = ["TECHNICAL", "PAYMENT", "BOOKING", "ACCOUNT", "OTHER"];
      if (categoriesCfg) {
        try {
          loadedCats = JSON.parse(categoriesCfg.value);
        } catch {}
      }
      setCategories(loadedCats);

      const matrixCfg = configs.find(c => c.key === 'SUPPORT_PRIORITY_MATRIX');
      let loadedMatrix: Record<string, 'LOW' | 'MEDIUM' | 'HIGH'> = {};
      if (matrixCfg) {
        try {
          const arr = JSON.parse(matrixCfg.value);
          arr.forEach((m: any) => {
            loadedMatrix[m.category.toUpperCase()] = m.priority.toUpperCase() as any;
          });
        } catch {}
      }
      
      loadedCats.forEach(cat => {
        if (!loadedMatrix[cat]) {
          const upper = cat.toUpperCase();
          if (upper === 'TECHNICAL' || upper === 'PAYMENT') loadedMatrix[cat] = 'HIGH';
          else if (upper === 'BOOKING') loadedMatrix[cat] = 'MEDIUM';
          else loadedMatrix[cat] = 'LOW';
        }
      });
      setMatrix(loadedMatrix);
    } catch {
      showToast('Failed to load priority matrix config', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { updateConfig } = await import('../services/adminService');
      const arr = categories.map(cat => ({
        category: cat,
        priority: matrix[cat] || 'LOW'
      }));

      const payload = [{
        key: 'SUPPORT_PRIORITY_MATRIX',
        value: JSON.stringify(arr),
        category: 'general'
      }];

      await updateConfig(payload);
      showToast('Priority Matrix saved successfully');
    } catch {
      showToast('Failed to save Priority Matrix', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading priority rules…</div>;

  return (
    <div style={{ padding: '8px 12px' }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 700, color: 'hsl(var(--foreground))' }}>Priority Allocation Matrix</h3>
      <p style={{ margin: '0 0 24px 0', fontSize: 13, color: 'hsl(var(--muted-foreground))', lineHeight: 1.5 }}>
        Specify the severity priority level automatically assigned when a scholar or customer opens a support ticket under a given category.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 500, marginBottom: 24 }}>
        {categories.map(cat => (
          <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'hsl(var(--muted)/0.2)', borderRadius: 8, border: '1px solid hsl(var(--border))' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'hsl(var(--foreground))' }}>{cat}</span>
            <select
              value={matrix[cat] || 'LOW'}
              onChange={e => setMatrix(prev => ({ ...prev, [cat]: e.target.value as any }))}
              style={{ ...inputStyle, width: 140, marginBottom: 0, padding: '6px 10px', fontSize: 12, background: '#fff' }}
            >
              <option value="LOW">🟢 LOW</option>
              <option value="MEDIUM">🟡 MEDIUM</option>
              <option value="HIGH">🔴 HIGH</option>
            </select>
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{ ...primaryBtn, padding: '10px 24px', fontSize: 13 }}
      >
        {saving ? 'Saving Matrix...' : 'Save Priority Matrix'}
      </button>
    </div>
  );
};

/* ─── Escalation Matrix Configuration Panel ──────────────────────── */
const EscalationMatrixPanel = ({ showToast }: { showToast: any }) => {
  const [slaHighHrs, setSlaHighHrs] = useState(2);
  const [slaHighMins, setSlaHighMins] = useState(0);
  const [slaMediumHrs, setSlaMediumHrs] = useState(6);
  const [slaMediumMins, setSlaMediumMins] = useState(0);
  const [slaLowHrs, setSlaLowHrs] = useState(12);
  const [slaLowMins, setSlaLowMins] = useState(0);

  const [action, setAction] = useState('REASSIGN_TO_SUPERVISOR');
  const [targetRole, setTargetRole] = useState('ADMIN');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const { getConfig } = await import('../services/adminService');
      const configs = await getConfig('general');

      const escalationCfg = configs.find(c => c.key === 'SUPPORT_ESCALATION_MATRIX');
      if (escalationCfg) {
        try {
          const parsed = JSON.parse(escalationCfg.value);
          if (parsed.slaMinutes) {
            setSlaHighHrs(Math.floor(parsed.slaMinutes.HIGH / 60));
            setSlaHighMins(parsed.slaMinutes.HIGH % 60);
            setSlaMediumHrs(Math.floor(parsed.slaMinutes.MEDIUM / 60));
            setSlaMediumMins(parsed.slaMinutes.MEDIUM % 60);
            setSlaLowHrs(Math.floor(parsed.slaMinutes.LOW / 60));
            setSlaLowMins(parsed.slaMinutes.LOW % 60);
          } else if (parsed.slaHours) {
            setSlaHighHrs(parsed.slaHours.HIGH ?? 2);
            setSlaHighMins(0);
            setSlaMediumHrs(parsed.slaHours.MEDIUM ?? 6);
            setSlaMediumMins(0);
            setSlaLowHrs(parsed.slaHours.LOW ?? 12);
            setSlaLowMins(0);
          }
          if (parsed.escalationAction) setAction(parsed.escalationAction);
          if (parsed.escalationTargetRole) setTargetRole(parsed.escalationTargetRole);
        } catch {}
      }
    } catch {
      showToast('Failed to load escalation matrix config', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { updateConfig } = await import('../services/adminService');
      const highTotal = (slaHighHrs * 60) + slaHighMins;
      const mediumTotal = (slaMediumHrs * 60) + slaMediumMins;
      const lowTotal = (slaLowHrs * 60) + slaLowMins;

      const valueObj = {
        slaMinutes: {
          HIGH: highTotal,
          MEDIUM: mediumTotal,
          LOW: lowTotal
        },
        slaHours: {
          HIGH: Math.round(highTotal / 60),
          MEDIUM: Math.round(mediumTotal / 60),
          LOW: Math.round(lowTotal / 60)
        },
        escalationAction: action,
        escalationTargetRole: targetRole
      };

      const payload = [{
        key: 'SUPPORT_ESCALATION_MATRIX',
        value: JSON.stringify(valueObj),
        category: 'general'
      }];

      await updateConfig(payload);
      showToast('Escalation SLA Matrix saved successfully');
    } catch {
      showToast('Failed to save escalation configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading SLA matrix…</div>;

  return (
    <div style={{ padding: '8px 12px' }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 700, color: 'hsl(var(--foreground))' }}>Escalation & SLA Matrix</h3>
      <p style={{ margin: '0 0 24px 0', fontSize: 13, color: 'hsl(var(--muted-foreground))', lineHeight: 1.5 }}>
        Define response deadline SLA rules in Hours and Minutes, and choose production-ready actions to execute when a ticket violates its SLA.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 500, marginBottom: 28 }}>
        <div>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 12, fontWeight: 800, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.02em' }}>SLA BREACH TIMEOUTS</h4>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* High Priority */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))' }}>🔴 High Priority SLA</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  value={slaHighHrs}
                  onChange={e => setSlaHighHrs(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{ ...inputStyle, width: 60, textAlign: 'center', marginBottom: 0, padding: '6px' }}
                />
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>hrs</span>
                <input
                  type="number"
                  value={slaHighMins}
                  onChange={e => setSlaHighMins(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                  style={{ ...inputStyle, width: 60, textAlign: 'center', marginBottom: 0, padding: '6px' }}
                />
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>mins</span>
              </div>
            </div>

            {/* Medium Priority */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))' }}>🟡 Medium Priority SLA</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  value={slaMediumHrs}
                  onChange={e => setSlaMediumHrs(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{ ...inputStyle, width: 60, textAlign: 'center', marginBottom: 0, padding: '6px' }}
                />
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>hrs</span>
                <input
                  type="number"
                  value={slaMediumMins}
                  onChange={e => setSlaMediumMins(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                  style={{ ...inputStyle, width: 60, textAlign: 'center', marginBottom: 0, padding: '6px' }}
                />
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>mins</span>
              </div>
            </div>

            {/* Low Priority */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))' }}>🟢 Low Priority SLA</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  value={slaLowHrs}
                  onChange={e => setSlaLowHrs(Math.max(0, parseInt(e.target.value) || 0))}
                  style={{ ...inputStyle, width: 60, textAlign: 'center', marginBottom: 0, padding: '6px' }}
                />
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>hrs</span>
                <input
                  type="number"
                  value={slaLowMins}
                  onChange={e => setSlaLowMins(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                  style={{ ...inputStyle, width: 60, textAlign: 'center', marginBottom: 0, padding: '6px' }}
                />
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>mins</span>
              </div>
            </div>
          </div>
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid hsl(var(--border))', margin: '4px 0' }} />

        <div>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 12, fontWeight: 800, color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Escalation Rule Trigger Settings</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: 'hsl(var(--muted-foreground))', marginBottom: 6 }}>ESCALATION ACTION</label>
              <select
                value={action}
                onChange={e => setAction(e.target.value)}
                style={{ ...inputStyle, width: '100%', padding: '10px 14px', background: '#fff', borderRadius: 6 }}
              >
                <option value="UPGRADE_PRIORITY">Upgrade Priority Tier (Low → Med → High)</option>
                <option value="REASSIGN_TO_SUPERVISOR">Reassign to Supervisor Admin</option>
                <option value="UPGRADE_AND_REASSIGN">Upgrade Priority & Reassign to Supervisor</option>
                <option value="SEND_REPRESENTATIVE_ALERT">Add SLA Warning to Representative Internal Notes</option>
                <option value="AUTO_REPLY_AND_WARNING">Send Apology Auto-Reply to Chat & Reassign</option>
                <option value="ESCALATE_TO_DISPUTE">Escalate Formally to Dispute System</option>
              </select>
            </div>

            {(action === 'REASSIGN_TO_SUPERVISOR' || action === 'UPGRADE_AND_REASSIGN' || action === 'AUTO_REPLY_AND_WARNING') && (
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: 'hsl(var(--muted-foreground))', marginBottom: 6 }}>TARGET SUPERVISOR ROLE</label>
                <select
                  value={targetRole}
                  onChange={e => setTargetRole(e.target.value)}
                  style={{ ...inputStyle, width: '100%', padding: '10px 14px', background: '#fff', borderRadius: 6 }}
                >
                  <option value="ADMIN">ADMIN Role</option>
                  <option value="SUPER_ADMIN">SUPER_ADMIN Role</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{ ...primaryBtn, padding: '12px 28px', fontSize: 13, borderRadius: 6, fontWeight: 700 }}
      >
        {saving ? 'Saving SLA Config...' : 'Save Escalation SLA Rules'}
      </button>
    </div>
  );
};

export default PlatformConfigs;
